import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { 
  Building2, Wrench, Search, ArrowRight, AlertTriangle, 
  CheckCircle, Clock, Settings, Shield, Truck
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperationsSelectPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [malls, setMalls] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [mallStats, setMallStats] = useState({});

  useEffect(() => {
    if (token) {
      fetchMalls();
    }
  }, [token]);

  const fetchMalls = async () => {
    try {
      const response = await axios.get(`${API}/api/malls`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMalls(response.data);
      
      // Fetch operations stats for each mall
      const stats = {};
      for (const mall of response.data.slice(0, 10)) {
        try {
          const dashRes = await axios.get(`${API}/api/operations/dashboard/${mall.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          stats[mall.id] = dashRes.data;
        } catch (e) {
          stats[mall.id] = null;
        }
      }
      setMallStats(stats);
    } catch (err) {
      console.error("Failed to fetch malls:", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredMalls = malls.filter(mall => 
    mall.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    mall.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
          <p className="text-muted-foreground">Loading malls...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="operations-select-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">Mall Operations & Facility Management</h1>
        <p className="text-muted-foreground">Select a mall to manage operations, maintenance, security, and vendors</p>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input 
          placeholder="Search malls..." 
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="mall-search-input"
        />
      </div>

      {/* Quick Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-red-500/10 to-red-500/5 border-red-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8 text-red-500" />
              <div>
                <p className="text-2xl font-bold">{Object.values(mallStats).reduce((sum, s) => sum + (s?.open_incidents || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Total Open Incidents</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Wrench className="w-8 h-8 text-orange-500" />
              <div>
                <p className="text-2xl font-bold">{Object.values(mallStats).reduce((sum, s) => sum + (s?.open_maintenance_jobs || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Open Maintenance Jobs</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500/10 to-green-500/5 border-green-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Settings className="w-8 h-8 text-green-500" />
              <div>
                <p className="text-2xl font-bold">{Object.values(mallStats).reduce((sum, s) => sum + (s?.total_assets || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Total Assets</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-blue-500" />
              <div>
                <p className="text-2xl font-bold">{Object.values(mallStats).reduce((sum, s) => sum + (s?.pending_work_permits || 0), 0)}</p>
                <p className="text-xs text-muted-foreground">Pending Work Permits</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Mall Cards */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredMalls.map(mall => {
          const stats = mallStats[mall.id];
          return (
            <Card 
              key={mall.id} 
              className="hover:border-primary/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/mall/${mall.id}/operations`)}
              data-testid={`mall-card-${mall.id}`}
            >
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Building2 className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-base">{mall.name}</CardTitle>
                      <CardDescription>{mall.city}, {mall.state}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {stats ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-lg font-bold text-red-400">{stats.open_incidents}</p>
                        <p className="text-xs text-muted-foreground">Incidents</p>
                      </div>
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-lg font-bold text-orange-400">{stats.open_maintenance_jobs}</p>
                        <p className="text-xs text-muted-foreground">Jobs</p>
                      </div>
                      <div className="p-2 bg-muted/50 rounded">
                        <p className="text-lg font-bold text-green-400">{stats.total_assets}</p>
                        <p className="text-xs text-muted-foreground">Assets</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">SLA Compliance</span>
                      <Badge variant={stats.sla_compliance_percent >= 90 ? "default" : "destructive"}>
                        {stats.sla_compliance_percent}%
                      </Badge>
                    </div>
                    
                    {(stats.critical_incidents > 0 || stats.overdue_jobs > 0) && (
                      <div className="flex gap-2">
                        {stats.critical_incidents > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            {stats.critical_incidents} Critical
                          </Badge>
                        )}
                        {stats.overdue_jobs > 0 && (
                          <Badge variant="outline" className="text-xs border-orange-500 text-orange-400">
                            {stats.overdue_jobs} Overdue
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p className="text-sm">No operations data yet</p>
                  </div>
                )}
                
                <Button variant="ghost" className="w-full mt-4 group" data-testid={`manage-ops-btn-${mall.id}`}>
                  Manage Operations
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredMalls.length === 0 && (
        <Card className="p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
          <p className="text-muted-foreground">No malls found</p>
        </Card>
      )}
    </div>
  );
}
